'use client'

import React, { useRef, useState } from 'react'
import Image from 'next/image'
import { imgproxyEnabled, imgproxyLoader } from '@/utilities/imgproxy'
import { useInView } from 'react-intersection-observer'
import { useScroll, useTransform, motion } from 'motion/react'
import AnimatedText from '@/components/Antonie/Components/animated-text'
import { Button } from '@/components/ui/button'
import { Play } from 'lucide-react'
import { IframeBlock as IframeBlockType } from '@/payload-types'
import { getMediaUrl } from '@/utilities/getMediaUrl'
import { getAltTag } from '@/utilities/getAltTag'

type Props = IframeBlockType & {
  className?: string
}

export const IframeBlockComponent: React.FC<Props> = ({
  className,
  code,
  image,
  title,
  teaser,
  description,
}) => {
  const [isIframeVisible, setIsIframeVisible] = useState(false)
  const sectionRef = useRef(null)
  const [contentRef, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  })

  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.5])
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1, 0.8])
  const y = useTransform(scrollYProgress, [0, 0.5, 1], ['5%', '0%', '5%'])

  const titleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
    },
  }

  const lineVariants = {
    hidden: { width: 0 },
    visible: {
      width: '4rem',
    },
  }

  const buttonVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
    },
  }

  const textOverlayVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
    },
  }

  const handleButtonClick = () => {
    setIsIframeVisible(true)
  }

  return (
    <motion.section ref={sectionRef} className="overflow-hidden bg-stone-100 py-12 md:py-24">
      <div className="container mx-auto px-6">
        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={titleVariants}
          className="mb-4 text-center md:mb-16"
        >
          <AnimatedText
            type="h2"
            variant="simple"
            classNameText="text-2xl md:text-4xl"
            text={title}
            className=" font-heading text-2xl font-medium text-gray-900 md:text-4xl"
            delay={0.2}
          />
        </motion.div>

        <motion.div
          ref={contentRef}
          style={{ opacity, y }}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="relative mx-auto max-w-7xl overflow-hidden shadow-xl"
        >
          <div className="group relative w-full">
            {isIframeVisible ? (
              <div className="h-full w-full" dangerouslySetInnerHTML={{ __html: code }} />
            ) : (
              <div className="group relative aspect-video w-full">
                <Image
                  loader={imgproxyEnabled ? imgproxyLoader : undefined}
                  src={getMediaUrl(image)}
                  alt={getAltTag(image, title)}
                  width={1200}
                  height={600}
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 transition-all duration-500 group-hover:bg-black/40">
                  <motion.div variants={buttonVariants}>
                    <Button
                      size="lg"
                      onClick={handleButtonClick}
                      className="flex h-16 w-16 items-center justify-center bg-white/95 text-gray-900 shadow-lg transition-all duration-500 hover:scale-105 hover:bg-white !p-0 rounded-full"
                      aria-label="Open Iframe"
                    >
                      <Play className="ml-1 h-8 w-8 text-black" />
                    </Button>
                  </motion.div>
                </div>
              </div>
            )}
          </div>
          {!isIframeVisible && (teaser || description) && (
            <motion.div
              variants={textOverlayVariants}
              className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-6 text-white"
            >
              {teaser && <p className="font-heading text-lg font-medium tracking-wide">{teaser}</p>}
              {description && (
                <p className="mt-1 font-sans text-sm font-light text-white/90">{description}</p>
              )}
            </motion.div>
          )}
        </motion.div>
      </div>
    </motion.section>
  )
}
